import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  liveOfferRebuildJobResultSchema,
  routes,
  SYNC_JOB_STALL_MS,
  type LiveOfferRebuildReport,
  type LiveOfferRebuildSubmissionReceipt,
  type MarketplaceId,
  type MiraklOfferRebuildCsv,
  type MiraklOfferRebuildReport,
  type MiraklOfferRebuildRow,
  type MiraklOfferRebuildUpload,
  type MiraklOfferRebuildVerification,
  type OctopiaLiveOfferRebuildReport,
  type OctopiaLiveOfferRebuildRow,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

// The Baselinker panel storage refresh: push the assigned storage's price / QTY
// onto a shop's live marketplace offers. Two phases — a read-only `preview`, then
// a `commit` that starts the panel's bulk operation — and BOTH are server-owned
// background jobs, so this hook starts one and polls it by id. The work outlives
// any single request (a commit re-reads offers until QTY converges), which is why
// there is nothing to await on the POST beyond a 202.
//
// The same job also runs marketplace live-offer rebuilds (`mode: "offer-feed"`).
// They read the shop's own live offers, move quantity at or below the reviewed
// floor to 0, and preserve every higher quantity. Preview keeps the exact
// marketplace artifact. Commit exists only where upload is activated. One job
// per shop covers both operations, so they cannot write the same offers at once.

/** How the refresh's outcome was established. A price operation can only ever be
 *  reported as accepted — the panel does not expose the expected storage price on
 *  an offer row, so there is nothing to compare against. */
type RefreshVerification =
  | { kind: "offer-reread"; offers: number }
  | { kind: "operation-accepted-unverified"; offers: number }

export type RefreshOperation = {
  kind: "price" | "quantity"
  /** Baselinker's own bulk-operation id — the handle for checking it in the panel. */
  operationId: string
  verification: RefreshVerification
}

/** One offer as the panel's listing row showed it BEFORE the operation ran, with
 *  the catalog prices joined on by the API. */
export type RefreshSelection = {
  id: string
  sku: string
  name: string
  currentQuantity: number | null
  soldQuantity: number | null
  storageQuantity: number | null
  currentPrice: string | null
  currency: string | null
  /** `products.price` for this shop's store — the authoritative marketplace
   *  price, and the one the table compares Baselinker against. Null when the SKU
   *  is not in the catalog mirror for that store. */
  basePrice: string | null
  /** `products.price_final` when a Magento promo is live, else null. Shown as a
   *  note next to the base price, never compared against: promo pricing does not
   *  reach a marketplace listing. */
  discountedPrice: string | null
  /** False when the offer's SKU has no mirrored product row for this store. */
  inCatalog: boolean
}

export type RefreshReport = {
  service: string
  site: string
  accountId: string
  tab: "ongoing"
  /** Ongoing offers in the mapped account. */
  offers: number
  /** How many of them this run selected. */
  selected: number
  selectedOffers: RefreshSelection[]
  planned: ("price" | "quantity")[]
  committed: boolean
  jobs: RefreshOperation[]
  /** Offers a quantity operation leaves alone because their listing row carried
   *  no storage QTY, so there is nothing to verify the result against. Reported
   *  on preview too, so the scope is known before committing. */
  skipped?: { id: string; sku: string; reason: "unreadable-storage-quantity" }[]
  /** The Magento store the catalog prices were read from; null when it could not
   *  be resolved, in which case every catalog price is null. */
  priceStore: string | null
}

export type RebuildRow = MiraklOfferRebuildRow
export type RebuildVerification = MiraklOfferRebuildVerification
export type RebuildCsv = MiraklOfferRebuildCsv
export type RebuildUpload = MiraklOfferRebuildUpload
export type MiraklRebuildReport = MiraklOfferRebuildReport
export type OctopiaRebuildRow = OctopiaLiveOfferRebuildRow
export type OctopiaRebuildReport = OctopiaLiveOfferRebuildReport
export type RebuildReport = LiveOfferRebuildReport
type RebuildSubmissionReceipt = LiveOfferRebuildSubmissionReceipt

/** The panel bulk-operation modes — what "Sync" runs. */
export type RefreshMode = "quantity" | "price" | "price-quantity"
/** Every mode the job accepts: a bulk operation, or the feed rebuild. */
type RefreshJobMode = RefreshMode | "offer-feed"
type RefreshPhase = "preview" | "commit"

type RefreshJobBase = {
  id: string
  scope: string
  phase: RefreshPhase
  status: "running" | "done" | "error" | "interrupted"
  startedBy: string
  startedAt: string
  updatedAt: string
  finishedAt: string | null
  error: string | null
}

/** A followed run. The `mode` decides which report `result` carries, so the UI
 *  has to narrow before reading it — the two reports share nothing. */
export type RefreshJob = StorageRefreshJob | RebuildJob
export type StorageRefreshJob = RefreshJobBase & {
  mode: RefreshMode
  result: RefreshReport | null
}
export type RebuildJob = RefreshJobBase & {
  mode: "offer-feed"
  result: RebuildReport | RebuildSubmissionReceipt | null
}

/** The storage-refresh report of a job, or null when the job is a feed rebuild
 *  (or has not reported yet). */
export function storageRefreshOf(job: RefreshJob | null): RefreshReport | null {
  return job === null || job.mode === "offer-feed" ? null : job.result
}

/** The rebuild report of a job, or null when the job is a storage refresh (or has
 *  not reported yet). A rebuild row written by an older build reads back with a
 *  null result, so this returns null for it too rather than a shape the table
 *  would read blind. */
export function rebuildOf(job: RefreshJob | null): RebuildReport | null {
  return job !== null && job.mode === "offer-feed" && job.result !== null && "kind" in job.result
    ? job.result
    : null
}

/** Durable Octopia submission proof retained even when package polling fails. */
export function rebuildSubmissionReceiptOf(
  job: RefreshJob | null
): RebuildSubmissionReceipt | null {
  return job !== null &&
    job.mode === "offer-feed" &&
    job.result !== null &&
    "submitted" in job.result &&
    job.result.submitted === true &&
    "packageId" in job.result
    ? job.result
    : null
}

export type RefreshSupport =
  | { supported: true; service: string; site: string; accountId: string }
  | { supported: false; reason: string }

/** A job WITHOUT its report — what availability carries. The report (every offer
 *  row, plus the kept CSV) is heavy and always rendered from the poll-by-id
 *  read, so availability only says which run to follow. */
export type RefreshJobRef = RefreshJobBase & { mode: RefreshJobMode }

/** Whether this shop's live offers can be rebuilt, and whether the marketplace's
 *  reviewed upload path is activated. Independent of Baselinker availability. */
export type RebuildSupport =
  | {
      supported: true
      source: "Mirakl" | "Octopia"
      artifactFormat: "csv" | "json"
      upload: { supported: true } | { supported: false; reason: string }
    }
  | { supported: false; reason: string }

export type RefreshAvailability = {
  support: RefreshSupport
  /** False when the API itself has no panel credentials — the integration is off
   *  for every shop, not just this one. Governs the SYNC half only. */
  panelConfigured: boolean
  rebuild: RebuildSupport
  /** Newest storage refresh for this shop; never a feed rebuild. The API keeps
   *  the two apart so a reader follows the kind it understands. */
  latest: RefreshJobRef | null
  /** Newest feed rebuild for this shop. */
  latestRebuild: RefreshJobRef | null
}

/** The shop's most recent run of either kind. The card follows one job at a
 *  time, and "the last thing that ran here" is what an operator expects to see. */
function newestOf(availability: RefreshAvailability | undefined): RefreshJobRef | null {
  const runs = [availability?.latest, availability?.latestRebuild].filter(
    (run): run is RefreshJobRef => run !== null && run !== undefined
  )
  return (
    runs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0] ??
    null
  )
}

const POLL_INTERVAL_MS = 2_500

async function fetchJson<T>(url: string, init?: { method: "POST"; body: unknown }): Promise<T> {
  const result = await apiFetch<T>(url, init ?? {})
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value
}

function parseRebuildResult(job: RefreshJob): RefreshJob {
  if (job.mode !== "offer-feed" || job.result === null) {
    return job
  }
  const parsed = liveOfferRebuildJobResultSchema.safeParse(job.result)
  if (!parsed.success) {
    throw new Error("The live-offer rebuild report returned by the API is unreadable.")
  }
  return { ...job, result: parsed.data }
}

/** Whether this shop can be refreshed at all, plus its last run. Asked before
 *  the control is offered, so an unmapped shop renders disabled with the reason
 *  instead of failing on click. */
function useBaselinkerRefreshAvailability(marketplace: MarketplaceId) {
  return useQuery({
    queryKey: queryKeys.baselinkerRefresh(marketplace),
    queryFn: () =>
      fetchJson<RefreshAvailability>(routes.marketplaces.baselinkerRefresh({ marketplace })),
  })
}

/** True when a `running` row has stopped being touched for longer than the
 *  shared stall window — its runner is dead by definition (see
 *  @nova-ai/shared sync-job.ts). The UI needs this because a frozen row would
 *  otherwise read as "running" forever and keep the control disabled; a fresh
 *  start supersedes the dead row server-side. */
export function isStalled(job: RefreshJob | null): boolean {
  if (job?.status !== "running") {
    return false
  }
  return Date.now() - new Date(job.updatedAt).getTime() > SYNC_JOB_STALL_MS
}

/** Drives one shop's refresh: start a phase, then follow the job it returns.
 *
 *  Reattaches on mount to a run already in flight (from `availability.latest`),
 *  so navigating away and back — or reloading — shows the live run rather than an
 *  untouched control. That is only possible because the job is server-owned. */
export function useBaselinkerRefresh(marketplace: MarketplaceId) {
  const qc = useQueryClient()
  const availability = useBaselinkerRefreshAvailability(marketplace)
  const [followedJob, setFollowedJob] = useState<{
    marketplace: MarketplaceId
    jobId: string
  } | null>(null)
  // Route params can change without remounting this hook. Scope the local
  // handle so the new shop never follows the old shop's preview or write.
  const jobId = followedJob?.marketplace === marketplace ? followedJob.jobId : null

  // Adopt whatever the server says is the newest run for this shop until the
  // operator starts one here — whichever of the two kinds ran last. Includes
  // terminal runs, so a finished preview's table survives a reload.
  const latestId = newestOf(availability.data)?.id ?? null
  useEffect(() => {
    if (latestId !== null) {
      setFollowedJob((current) =>
        current?.marketplace === marketplace ? current : { marketplace, jobId: latestId }
      )
    }
  }, [latestId, marketplace])

  const job = useQuery({
    queryKey: queryKeys.baselinkerRefreshJob(jobId ?? ""),
    enabled: jobId !== null,
    queryFn: async () =>
      parseRebuildResult(
        await fetchJson<RefreshJob>(routes.marketplaces.baselinkerRefreshJob({ id: jobId ?? "" }))
      ),
    // Poll only while the run is alive. A stalled row stops the polling too:
    // nothing will ever change it, and the control re-enables instead.
    refetchInterval: (query) => {
      const data = query.state.data
      return data?.status === "running" && !isStalled(data) ? POLL_INTERVAL_MS : false
    },
  })

  // Once a run reaches a terminal status, re-read availability so `latest` (and
  // the caption built from it) matches what the poll just saw.
  const status = job.data?.status
  useEffect(() => {
    if (status !== undefined && status !== "running") {
      void qc.invalidateQueries({ queryKey: queryKeys.baselinkerRefresh(marketplace) })
    }
  }, [status, qc, marketplace])

  const start = useMutation({
    mutationFn: (input: {
      mode: RefreshJobMode
      phase: RefreshPhase
      /** The reviewed rebuild preview a rebuild commit uploads the bytes of. */
      reviewedJobId?: string
      /** Inclusive floor for a rebuild preview. */
      zeroAtOrBelow?: number
    }) =>
      fetchJson<RefreshJob>(routes.marketplaces.baselinkerRefreshJobs({ marketplace }), {
        method: "POST",
        body: input,
      }).then(parseRebuildResult),
    onSuccess: (started) => {
      setFollowedJob({ marketplace, jobId: started.id })
      qc.setQueryData(queryKeys.baselinkerRefreshJob(started.id), started)
    },
  })

  const current = job.data ?? null
  const newest = newestOf(availability.data)
  return {
    availability,
    /** When this shop last finished a run of either kind — the card's caption. */
    lastFinishedAt: newest?.finishedAt ?? null,
    /** The run being followed: the one just started, or the shop's newest. */
    job: current,
    /** True while a run is genuinely alive — a dead row does not count. */
    isRunning: current?.status === "running" && !isStalled(current),
    /** True when the followed run's row froze past the stall window. */
    isStalled: isStalled(current),
    start,
    /** Drop the followed run from view (after acting on a preview). */
    dismiss: () => setFollowedJob(null),
  }
}
